import {Fade, Slide} from '@aboutyou/transitions';
import {styled} from '@linaria/react';
import React from 'react';
import {createPortal} from 'react-dom';
import {COLORS} from '../../../constants/colors';
import {tabletAndUpLinaria} from '../../../constants/customMedia';
import {Z_INDEXES} from '../../../constants/layers';
import {useLockBodyScroll} from '../../../hooks/useLockBodyScroll/useLockBodyScroll';
import {useDrawerPortalElement} from '../../../hooks/usePortalElement/useDrawerPortalElement';
import {BlurredOverlay} from '../BlurredOverlay/BlurredOverlay';
import {UnstyledButton} from '../Buttons/UnstyledButton';
import {ContentPopperBaseProps} from '../ContentPopper/ContentPopper';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';

export const ANIMATION_DURATION = 450;

export interface DrawerProps extends ContentPopperBaseProps {
  slideDirection?: 'left' | 'right';
  OverlayComponent?: React.ComponentType<
    React.ComponentProps<typeof BlurredOverlay>
  >;
  ref?: React.RefObject<HTMLDivElement>;
  setTransparentHeaderBackground?: boolean;
  hideCloseButton?: boolean;
}

export const Drawer = React.forwardRef<HTMLDivElement, DrawerProps>(
  function DrawerComponent(
    {
      dataTestId,
      headerContent,
      bodyContent,
      onClose,
      open,
      slideDirection = 'right',
      renderInPortal,
      OverlayComponent = BlurredOverlay,
      showHeaderDivider = true,
      hideBodyBorderTop,
      hideHeader,
      hideCloseButton,
      ...rest
    },
    ref,
  ) {
    useLockBodyScroll({lockScroll: open});

    const drawerContent = (
      <>
        <Fade mounted={open} duration={ANIMATION_DURATION}>
          <OverlayComponent
            $zIndex={Z_INDEXES.OFF_CANVAS}
            data-testid="drawerBackdrop"
            onClick={e => {
              e.stopPropagation();
              onClose();
            }}
          />
        </Fade>
        <Slide
          mounted={open}
          direction={slideDirection}
          duration={ANIMATION_DURATION}
        >
          <FixedContainer
            aria-modal="true"
            role="dialog"
            tabIndex={-1}
            data-direction={slideDirection}
            visible={open}
            {...rest}
          >
            <DrawerContainer data-testid={dataTestId} ref={ref}>
              {!hideHeader && (
                <DrawerHeaderContainer $showHeaderDivider={showHeaderDivider}>
                  <DrawerHeaderContent>{headerContent}</DrawerHeaderContent>
                  {!hideCloseButton && (
                    <CloseButton
                      onClick={onClose}
                      data-testid="drawerCloseIcon"
                    >
                      <Icon asset={GenericIconName.Close} size="S" />
                    </CloseButton>
                  )}
                </DrawerHeaderContainer>
              )}
              {Array.isArray(bodyContent) ? (
                bodyContent
                  .filter(section => !!section)
                  .map((bodySection, key) => {
                    return (
                      <React.Fragment key={key}>{bodySection}</React.Fragment>
                    );
                  })
              ) : (
                <DrawerBody $hideBodyBorderTop={hideBodyBorderTop}>
                  {bodyContent}
                </DrawerBody>
              )}
            </DrawerContainer>
          </FixedContainer>
        </Slide>
      </>
    );

    if (renderInPortal) {
      return <RenderInPortal>{drawerContent}</RenderInPortal>;
    }

    return <>{drawerContent}</>;
  },
);

const RenderInPortal: React.FC<React.PropsWithChildren> = ({children}) => {
  const drawerPortalElement = useDrawerPortalElement();

  if (!drawerPortalElement) {
    return null;
  }

  return createPortal(<>{children}</>, drawerPortalElement);
};

const DrawerHeaderContent = styled.div`
  height: 100%;
  flex-grow: 1;
  overflow: hidden;
  padding-left: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const DrawerContainer = styled.section`
  height: 100%;
  width: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;

  ${tabletAndUpLinaria('width: 400px;')};
`;

export const DrawerBody = styled.section<{$hideBodyBorderTop?: boolean}>`
  border-top: ${props =>
    props.$hideBodyBorderTop ? 'none' : `1px solid ${COLORS.Grey3}`};
  flex: 1 1 auto;
  padding: 0 20px 10px 20px;
`;

const DrawerHeaderContainer = styled.header<{
  $showHeaderDivider: boolean;
}>`
  display: flex;
  flex: 0 0 56px;
  background-color: ${COLORS.White};
  position: sticky;
  top: 0;
  z-index: ${Z_INDEXES.APP_SCROLL_TO_TOP};

  // box-shadow is used as a collapsible border
  box-shadow: ${({$showHeaderDivider}) =>
    $showHeaderDivider ? `0 1px 0 0 ${COLORS.Grey3}` : 'none'};
`;

const CloseButton = styled(UnstyledButton)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const FixedContainer = styled.aside<{
  visible: boolean;
}>`
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: ${({visible}) => (visible ? 'all' : 'none')};
  touch-action: none;
  overflow: clip;
  z-index: ${Z_INDEXES.DRAWER};
  left: 0;

  ${tabletAndUpLinaria(`
    max-width: 400px;
    border-radius: 0 10px 10px 0;
  `)}

  &[data-direction='left'] {
    right: 0;
    left: unset;

    ${tabletAndUpLinaria(`
      border-radius: 10px 0 0 10px;
    `)}
  }

  ${tabletAndUpLinaria(`
    border-radius: 0 10px 10px 0;
  `)}
`;
